/**
 * Purrrcathoodie storefront behaviour.
 *
 * Every interactive piece is a custom element, so the Shopify Theme Editor's
 * dynamic section load / unload / reorder is handled by the platform:
 * connectedCallback initialises, disconnectedCallback tears down. Nothing
 * initialises on DOMContentLoaded, nothing double-binds, and no globals are
 * created beyond the element definitions themselves.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  var DESKTOP_BP = 860;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /** Format cents with the shop's money_format string. */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    format = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function decimals(num, precision, thousands, decimal) {
      if (isNaN(num)) num = 0;
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      var value = (num / 100).toFixed(precision);
      var parts = value.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var tail = parts[1] ? decimal + parts[1] : '';
      return head + tail;
    }

    var match = format.match(placeholder);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount': value = decimals(cents, 2); break;
      case 'amount_no_decimals': value = decimals(cents, 0); break;
      case 'amount_with_comma_separator': value = decimals(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = decimals(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = decimals(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = decimals(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = decimals(cents, 2, ' ', ','); break;
      default: value = decimals(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /** Read a <script type="application/json"> payload safely. */
  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /** Add lines to the cart through Shopify's standard actions when present. */
  function addLines(lines) {
    var actions = window.Shopify && window.Shopify.actions;
    if (actions && actions.updateCart) {
      return actions.updateCart(
        { lines: lines.map(function (l) { return { merchandiseId: String(l.id), quantity: l.quantity }; }) },
        { event: { context: 'product' } }
      ).then(function (res) {
        if (res && res.userErrors && res.userErrors.length) {
          throw new Error(res.userErrors[0].message);
        }
        return res;
      });
    }
    // Fallback for storefronts without the standard-actions bundle.
    var url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines.map(function (l) { return { id: l.id, quantity: l.quantity }; }) })
    }).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) throw new Error(body.description || body.message || 'Cart error');
        document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', { bubbles: true }));
        return body;
      });
    });
  }

  /* ------------------------------------------------------- scroll reveal */

  var revealObserver = null;
  function revealIn(root) {
    var targets = $$('[data-pch-reveal]', root || document);
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(function (el) { el.classList.add('pch-revealed'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('pch-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
    }
    targets.forEach(function (el) {
      if (!el.classList.contains('pch-revealed')) revealObserver.observe(el);
    });
  }

  /**
   * Base class: gives every component reveal wiring plus a disposer list that
   * disconnectedCallback drains, so a Theme Editor unload leaves nothing behind.
   */
  function Disposable(Base) {
    return class extends Base {
      constructor() { super(); this._disposers = []; }
      on(target, type, handler, opts) {
        target.addEventListener(type, handler, opts);
        this._disposers.push(function () { target.removeEventListener(type, handler, opts); });
      }
      addDisposer(fn) { this._disposers.push(fn); }
      connectedCallback() { revealIn(this); }
      disconnectedCallback() {
        this._disposers.forEach(function (fn) { try { fn(); } catch (e) {} });
        this._disposers = [];
      }
    };
  }

  /* ------------------------------------------------------- announcement */

  class PchAnnouncement extends Disposable(HTMLElement) {
    connectedCallback() {
      super.connectedCallback();
      this.slides = $$('.pch-ann__msg', this);
      if (this.slides.length < 2) return;
      var speed = parseInt(this.getAttribute('data-speed'), 10) || 4000;
      var i = 0;
      var self = this;
      var timer = setInterval(function () {
        self.slides[i].classList.remove('is-active');
        i = (i + 1) % self.slides.length;
        self.slides[i].classList.add('is-active');
      }, speed);
      this.addDisposer(function () { clearInterval(timer); });
    }
  }

  /* ------------------------------------------------------------- header */

  class PchHeader extends Disposable(HTMLElement) {
    connectedCallback() {
      super.connectedCallback();
      var self = this;
      this.drawer = $('[data-pch-drawer]', this);
      var burger = $('[data-pch-burger]', this);

      if (burger && this.drawer) {
        this.on(burger, 'click', function () { self.openDrawer(); });
        this.on(this.drawer, 'click', function (e) {
          if (e.target === self.drawer || e.target.closest('[data-pch-drawer-close]')) self.closeDrawer();
          if (e.target.closest('a')) self.closeDrawer();
        });
        // <dialog> closes itself on Escape, so release the scroll lock and reset
        // the burger however the drawer was dismissed.
        this.on(this.drawer, 'close', function () {
          document.documentElement.style.overflow = '';
          burger.setAttribute('aria-expanded', 'false');
        });
      }

      // Cart button hands off to the theme's own drawer (or /cart).
      var cartBtn = $('[data-pch-cart]', this);
      if (cartBtn) {
        this.on(cartBtn, 'click', function (e) {
          var actions = window.Shopify && window.Shopify.actions;
          if (actions && actions.openCart) { e.preventDefault(); actions.openCart(); }
        });
      }

      // Search button opens the theme's search modal when it exists.
      var searchBtn = $('[data-pch-search]', this);
      if (searchBtn) {
        this.on(searchBtn, 'click', function (e) {
          var modal = document.querySelector('dialog#search-modal, [data-search-modal] dialog, search-modal dialog');
          if (modal && typeof modal.showModal === 'function') { e.preventDefault(); modal.showModal(); }
        });
      }

      this.syncCount();
      this.on(document, 'shopify:cart:lines-update', function () { self.syncCount(); });
    }

    isDrawerOpen() {
      return !!(this.drawer && this.drawer.open);
    }

    openDrawer() {
      if (!this.drawer || this.isDrawerOpen()) return;
      // Top layer, not a fixed overlay: the header sets backdrop-filter, which
      // makes it a containing block for fixed positioning, so a plain overlay
      // would be clipped to the height of the header bar.
      if (typeof this.drawer.showModal === 'function') this.drawer.showModal();
      else this.drawer.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      var burger = $('[data-pch-burger]', this);
      if (burger) burger.setAttribute('aria-expanded', 'true');
      var focusable = $('a, button', this.drawer);
      if (focusable) focusable.focus();
    }

    closeDrawer() {
      if (!this.drawer) return;
      if (typeof this.drawer.close === 'function' && this.drawer.open) this.drawer.close();
      else this.drawer.removeAttribute('open');
      document.documentElement.style.overflow = '';
      var burger = $('[data-pch-burger]', this);
      if (burger) {
        burger.setAttribute('aria-expanded', 'false');
        burger.focus();
      }
    }

    syncCount() {
      var el = $('[data-pch-cart-count]', this);
      if (!el) return;
      var actions = window.Shopify && window.Shopify.actions;
      if (actions && actions.getCart) {
        actions.getCart().then(function (res) {
          var n = (res && res.cart && res.cart.totalQuantity) || 0;
          el.textContent = String(n);
          el.hidden = n === 0;
        }).catch(function () {});
        return;
      }
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          el.textContent = String(c.item_count || 0);
          el.hidden = !c.item_count;
        })
        .catch(function () {});
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.documentElement.style.overflow = '';
    }
  }

  /* ------------------------------------------------------- main product */

  class PchProduct extends Disposable(HTMLElement) {
    connectedCallback() {
      super.connectedCallback();
      var self = this;

      this.variants = readJSON($('[data-pch-variants]', this)) || [];
      this.tiers = readJSON($('[data-pch-tiers]', this)) || [{ qty: 1, discount: 0, code: '' }];
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.optionCount = parseInt(this.getAttribute('data-option-count'), 10) || 1;
      this.updateUrl = this.getAttribute('data-update-url') !== 'false';

      this.form = $('[data-pch-form]', this);
      this.idInput = $('[data-pch-variant-id]', this);
      this.atc = $('[data-pch-atc]', this);
      this.stickyBtn = $('[data-pch-sticky-atc]', this);
      this.sticky = $('[data-pch-sticky]', this);
      this.units = $('[data-pch-units]', this);
      this.feedback = $('[data-pch-added]', this);
      this.errorBox = $('[data-pch-error]', this);

      this.tierIndex = 0;
      var checkedTier = $('[data-pch-tier]:checked', this);
      if (checkedTier) this.tierIndex = parseInt(checkedTier.value, 10) || 0;

      this.bindOptions();
      this.bindTiers();
      this.bindGallery();
      this.bindSizeGuide();
      this.bindSubmit();
      this.bindSticky();
      this.bindEditor();

      this.render();
    }

    /* --- option selection --- */

    selectedOptions(scope) {
      var root = scope || this;
      var values = [];
      for (var i = 1; i <= this.optionCount; i++) {
        var input = $('[data-pch-option="' + i + '"]:checked', root);
        values.push(input ? input.value : null);
      }
      return values;
    }

    findVariant(values) {
      return this.variants.find(function (v) {
        return values.every(function (val, i) { return val === null || v.options[i] === val; });
      }) || null;
    }

    bindOptions() {
      var self = this;
      $$('[data-pch-option]', this).forEach(function (input) {
        if (input.closest('[data-pch-unit]')) return; // unit pickers handled separately
        self.on(input, 'change', function () { self.render(); });
      });
    }

    /* --- bundle tiers --- */

    bindTiers() {
      var self = this;
      $$('[data-pch-tier]', this).forEach(function (input) {
        self.on(input, 'change', function () {
          self.tierIndex = parseInt(input.value, 10) || 0;
          self.render();
        });
      });
      // Unit pickers re-render the summary (availability warnings) on change.
      $$('[data-pch-unit] [data-pch-option]', this).forEach(function (input) {
        self.on(input, 'change', function () { self.renderUnits(); });
      });
    }

    currentTier() {
      return this.tiers[this.tierIndex] || this.tiers[0] || { qty: 1, discount: 0, code: '' };
    }

    /** Lines to add: one entry per hoodie in the tier, merged by variant id. */
    resolveLines() {
      var tier = this.currentTier();
      var qty = tier.qty || 1;
      var main = this.currentVariant;
      var picked = [];

      if (qty > 1 && this.units) {
        var cards = $$('[data-pch-unit]', this.units);
        for (var i = 0; i < qty; i++) {
          var card = cards[i];
          var variant = card ? this.findVariant(this.selectedOptions(card)) : main;
          picked.push(variant || main);
        }
      } else {
        for (var j = 0; j < qty; j++) picked.push(main);
      }

      var merged = [];
      picked.forEach(function (v) {
        if (!v) return;
        var hit = merged.find(function (m) { return m.id === v.id; });
        if (hit) hit.quantity += 1;
        else merged.push({ id: v.id, quantity: 1, variant: v });
      });
      return merged;
    }

    /* --- gallery --- */

    bindGallery() {
      var self = this;
      this.slides = $$('[data-pch-slide]', this);
      this.thumbs = $$('[data-pch-thumb]', this);
      if (!this.slides.length) return;

      this.mediaIndex = 0;

      this.thumbs.forEach(function (btn, i) {
        self.on(btn, 'click', function () { self.showMedia(i); });
      });
      var prev = $('[data-pch-prev]', this);
      var next = $('[data-pch-next]', this);
      if (prev) this.on(prev, 'click', function () { self.showMedia(self.mediaIndex - 1); });
      if (next) this.on(next, 'click', function () { self.showMedia(self.mediaIndex + 1); });

      // Keyboard support on the stage.
      var stage = $('[data-pch-stage]', this);
      if (stage) {
        this.on(stage, 'keydown', function (e) {
          if (e.key === 'ArrowLeft') { e.preventDefault(); self.showMedia(self.mediaIndex - 1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); self.showMedia(self.mediaIndex + 1); }
        });

        // Touch swipe.
        var startX = null, startY = null;
        this.on(stage, 'touchstart', function (e) {
          startX = e.touches[0].clientX; startY = e.touches[0].clientY;
        }, { passive: true });
        this.on(stage, 'touchend', function (e) {
          if (startX === null) return;
          var dx = e.changedTouches[0].clientX - startX;
          var dy = e.changedTouches[0].clientY - startY;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
            self.showMedia(self.mediaIndex + (dx < 0 ? 1 : -1));
          }
          startX = startY = null;
        }, { passive: true });
      }
    }

    showMedia(index) {
      if (!this.slides || !this.slides.length) return;
      var n = this.slides.length;
      index = ((index % n) + n) % n;
      this.mediaIndex = index;
      this.slides.forEach(function (s, i) {
        var active = i === index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
        // Pause any video that scrolls out of view.
        var media = s.querySelector('video');
        if (media && !active && !media.paused) media.pause();
      });
      this.thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    showMediaById(mediaId) {
      if (!mediaId || !this.slides) return;
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].getAttribute('data-media-id') === String(mediaId)) {
          this.showMedia(i);
          return;
        }
      }
    }

    /* --- size guide --- */

    bindSizeGuide() {
      var self = this;
      this.modal = $('[data-pch-sizeguide]', this);
      if (!this.modal) return;
      $$('[data-pch-sizeguide-open]', this).forEach(function (btn) {
        self.on(btn, 'click', function () {
          if (typeof self.modal.showModal === 'function') self.modal.showModal();
          else self.modal.setAttribute('open', '');
          self.syncSticky();
        });
      });
      $$('[data-pch-sizeguide-close]', this.modal).forEach(function (btn) {
        self.on(btn, 'click', function () { self.closeModal(); });
      });
      // Click on the backdrop area closes.
      this.on(this.modal, 'click', function (e) {
        if (e.target === self.modal || e.target.hasAttribute('data-pch-modal-scrim')) self.closeModal();
      });
      this.on(this.modal, 'close', function () { self.syncSticky(); });
    }

    closeModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function') this.modal.close();
      else this.modal.removeAttribute('open');
    }

    /* --- sticky CTA --- */

    bindSticky() {
      if (!this.sticky) return;
      var self = this;
      this.stickyThreshold = parseInt(this.getAttribute('data-sticky-after'), 10) || 620;
      var onScroll = function () { self.syncSticky(); };
      this.on(window, 'scroll', onScroll, { passive: true });
      this.on(window, 'resize', onScroll);
      this.syncSticky();
    }

    syncSticky() {
      if (!this.sticky) return;
      var modalOpen = this.modal && this.modal.open;
      var show = window.innerWidth < DESKTOP_BP &&
                 window.scrollY > this.stickyThreshold &&
                 !modalOpen;
      this.sticky.classList.toggle('is-visible', !!show);
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    /* --- submit --- */

    bindSubmit() {
      var self = this;
      if (this.form) {
        this.on(this.form, 'submit', function (e) {
          e.preventDefault();
          self.submit();
        });
      }
      if (this.stickyBtn) {
        this.on(this.stickyBtn, 'click', function (e) {
          e.preventDefault();
          self.submit();
        });
      }
    }

    submit() {
      var self = this;
      var lines = this.resolveLines();
      if (!lines.length) return;

      var unavailable = lines.filter(function (l) { return !l.variant.available; });
      if (unavailable.length) {
        this.showError(this.getAttribute('data-text-unavailable') || 'That combination is sold out.');
        return;
      }

      this.setLoading(true);
      this.hideError();

      addLines(lines)
        .then(function () {
          self.setLoading(false);
          self.showAdded(lines.reduce(function (n, l) { return n + l.quantity; }, 0));
          var tier = self.currentTier();
          if (tier.code) {
            // A real discount code is applied through Shopify's own route so the
            // saving shown on the page is the saving charged at checkout.
            window.location.href = '/discount/' + encodeURIComponent(tier.code) +
              '?redirect=' + encodeURIComponent((window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart');
          }
        })
        .catch(function (err) {
          self.setLoading(false);
          self.showError((err && err.message) || 'Something went wrong. Please try again.');
        });
    }

    setLoading(state) {
      [this.atc, this.stickyBtn].forEach(function (btn) {
        if (!btn) return;
        btn.classList.toggle('is-loading', state);
        btn.disabled = state;
        btn.setAttribute('aria-busy', state ? 'true' : 'false');
      });
    }

    showAdded(count) {
      if (!this.feedback) return;
      var tpl = this.feedback.getAttribute('data-template') || '✓ Added to your cart — [count] item(s)';
      this.feedback.textContent = tpl.replace('[count]', count).replace('{{ count }}', count);
      this.feedback.hidden = false;
      clearTimeout(this._addedTimer);
      var el = this.feedback;
      this._addedTimer = setTimeout(function () { el.hidden = true; }, 3200);
      this.addDisposer(function () { clearTimeout(this._addedTimer); }.bind(this));
    }

    showError(msg) {
      if (!this.errorBox) return;
      this.errorBox.textContent = msg;
      this.errorBox.hidden = false;
    }

    hideError() { if (this.errorBox) this.errorBox.hidden = true; }

    /* --- render --- */

    render() {
      var values = this.selectedOptions();
      var variant = this.findVariant(values);
      this.currentVariant = variant;

      this.renderAvailability(values);
      this.renderPrice(variant);
      this.renderTiers(variant);
      this.renderUnits();
      this.renderButton(variant);
      this.renderSelectedLabels();

      if (this.idInput) this.idInput.value = variant ? variant.id : '';

      if (variant && variant.featured_media_id) this.showMediaById(variant.featured_media_id);

      if (variant && this.updateUrl && window.history && history.replaceState && !window.Shopify?.designMode) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }

      this.dispatchEvent(new CustomEvent('pch:variant:change', {
        bubbles: true, detail: { variant: variant }
      }));
    }

    /**
     * Mark every option value that cannot combine with the current selection.
     * A value is unavailable when no variant matching the other selected
     * options has that value in stock.
     */
    renderAvailability(values) {
      var self = this;
      for (var i = 0; i < this.optionCount; i++) {
        (function (index) {
          $$('[data-pch-option="' + (index + 1) + '"]', self).forEach(function (input) {
            if (input.closest('[data-pch-unit]')) return;
            var probe = values.slice();
            probe[index] = input.value;
            // Only constrain by options chosen before this one, mirroring
            // Shopify's own progressive variant resolution.
            for (var k = index + 1; k < probe.length; k++) probe[k] = null;
            var match = self.variants.filter(function (v) {
              return probe.every(function (val, n) { return val === null || v.options[n] === val; });
            });
            var exists = match.length > 0;
            var inStock = match.some(function (v) { return v.available; });
            var wrapper = input.parentElement;
            input.disabled = !exists;
            if (wrapper) {
              wrapper.classList.toggle('pch-swatch--out', wrapper.classList.contains('pch-swatch') && (!exists || !inStock));
              wrapper.classList.toggle('pch-size--out', wrapper.classList.contains('pch-size') && (!exists || !inStock));
            }
            input.setAttribute('aria-disabled', (!exists || !inStock) ? 'true' : 'false');
          });
        })(i);
      }
    }

    renderPrice(variant) {
      var box = $('[data-pch-price]', this);
      if (!box) return;
      if (!variant) { box.setAttribute('data-on-sale', 'false'); return; }

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      box.setAttribute('data-on-sale', onSale ? 'true' : 'false');

      var now = $('[data-pch-price-now]', box);
      var was = $('[data-pch-price-was]', box);
      var save = $('[data-pch-price-save]', box);
      if (now) now.textContent = formatMoney(variant.price, this.moneyFormat);
      if (was) was.textContent = onSale ? formatMoney(variant.compare_at_price, this.moneyFormat) : '';
      if (save) {
        save.textContent = onSale
          ? 'SAVE ' + Math.round((1 - variant.price / variant.compare_at_price) * 100) + '%'
          : '';
      }
      var badge = $('[data-pch-save-badge]', this);
      if (badge) {
        badge.hidden = !onSale;
        if (onSale) {
          badge.textContent = 'SAVE ' + Math.round((1 - variant.price / variant.compare_at_price) * 100) + '%';
        }
      }
    }

    /** Tier pricing is derived from the live variant price — never hard-coded. */
    renderTiers(variant) {
      var self = this;
      if (!variant) return;
      $$('[data-pch-tier-card]', this).forEach(function (card) {
        var i = parseInt(card.getAttribute('data-pch-tier-card'), 10);
        var tier = self.tiers[i];
        if (!tier) return;
        var qty = tier.qty || 1;
        var discount = tier.discount || 0;
        var gross = variant.price * qty;
        var total = Math.round(gross * (1 - discount / 100));
        var compareUnit = (variant.compare_at_price && variant.compare_at_price > variant.price)
          ? variant.compare_at_price : null;
        var compare = compareUnit ? compareUnit * qty : (discount > 0 ? gross : null);

        var priceEl = $('[data-pch-tier-price]', card);
        var compareEl = $('[data-pch-tier-compare]', card);
        var perEl = $('[data-pch-tier-per]', card);

        if (priceEl) priceEl.textContent = formatMoney(total, self.moneyFormat);
        if (compareEl) compareEl.textContent = compare ? formatMoney(compare, self.moneyFormat) : '';
        if (perEl) {
          var savePct = compare ? Math.round((1 - total / compare) * 100) : 0;
          if (qty > 1) {
            perEl.textContent = formatMoney(Math.round(total / qty), self.moneyFormat) +
              ' each' + (savePct > 0 ? ' · save ' + savePct + '%' : '');
          } else {
            perEl.textContent = savePct > 0 ? 'Save ' + savePct + '%' : '';
          }
        }
      });
    }

    /** Show only as many unit cards as the selected tier needs. */
    renderUnits() {
      if (!this.units) return;
      var qty = this.currentTier().qty || 1;
      this.units.hidden = qty < 2;
      var cards = $$('[data-pch-unit]', this.units);
      var self = this;
      cards.forEach(function (card, i) {
        var used = i < qty;
        card.hidden = !used;
        card.querySelectorAll('input').forEach(function (input) { input.disabled = !used; });
        if (!used) return;
        var v = self.findVariant(self.selectedOptions(card));
        var warn = $('[data-pch-unit-warn]', card);
        if (warn) warn.hidden = !!(v && v.available);
      });
    }

    renderButton(variant) {
      var label = this.getAttribute('data-text-add') || 'Add to Cart';
      var soldOut = this.getAttribute('data-text-soldout') || 'Sold out';
      var unavailable = this.getAttribute('data-text-unavailable-short') || 'Unavailable';
      var tier = this.currentTier();
      var qty = tier.qty || 1;

      var available = !!(variant && variant.available);
      var text = variant ? (available ? label : soldOut) : unavailable;

      var labelEl = $('[data-pch-atc-label]', this);
      var priceEl = $('[data-pch-atc-price]', this);
      if (labelEl) labelEl.textContent = text;

      var total = null;
      if (variant) {
        total = Math.round(variant.price * qty * (1 - (tier.discount || 0) / 100));
      }
      if (priceEl) priceEl.textContent = available && total !== null ? formatMoney(total, this.moneyFormat) : '';
      var sep = $('[data-pch-atc-sep]', this);
      if (sep) sep.hidden = !(available && total !== null);

      // Accelerated checkout can only carry one variant, so it steps aside on multi-packs.
      var dynamic = $('[data-pch-dynamic]', this);
      if (dynamic) dynamic.hidden = qty > 1;

      if (this.atc) this.atc.disabled = !available;
      if (this.stickyBtn) this.stickyBtn.disabled = !available;

      // Sticky bar mirrors the live price.
      var sPrice = $('[data-pch-sticky-price]', this);
      var sCompare = $('[data-pch-sticky-compare]', this);
      if (sPrice) sPrice.textContent = total !== null ? formatMoney(total, this.moneyFormat) : '';
      if (sCompare) {
        var cmp = variant && variant.compare_at_price > variant.price ? variant.compare_at_price * qty : null;
        sCompare.textContent = cmp ? formatMoney(cmp, this.moneyFormat) : '';
      }
    }

    renderSelectedLabels() {
      var self = this;
      $$('[data-pch-selected-for]', this).forEach(function (el) {
        var n = el.getAttribute('data-pch-selected-for');
        var input = $('[data-pch-option="' + n + '"]:checked', self);
        el.textContent = input ? input.value : '';
      });
    }

    /* --- theme editor --- */

    bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      var self = this;
      // Selecting a gallery block in the editor brings that slide forward.
      this.on(document, 'shopify:block:select', function (e) {
        if (!self.contains(e.target)) return;
        var slide = e.target.closest('[data-pch-slide]');
        if (slide) self.showMedia(self.slides.indexOf(slide));
      });
    }
  }

  /* ---------------------------------------------------------------- FAQ */

  class PchFaq extends Disposable(HTMLElement) {
    connectedCallback() {
      super.connectedCallback();
      var self = this;
      this.items = $$('[data-pch-faq-q]', this);
      this.single = this.getAttribute('data-single') !== 'false';

      this.items.forEach(function (btn) {
        self.on(btn, 'click', function () { self.toggle(btn); });
      });

      if (window.Shopify && window.Shopify.designMode) {
        this.on(document, 'shopify:block:select', function (e) {
          if (!self.contains(e.target)) return;
          var btn = e.target.querySelector('[data-pch-faq-q]');
          if (btn) self.open(btn);
        });
      }
    }

    open(btn) {
      var self = this;
      if (this.single) {
        this.items.forEach(function (other) {
          if (other !== btn) other.setAttribute('aria-expanded', 'false');
        });
      }
      btn.setAttribute('aria-expanded', 'true');
    }

    toggle(btn) {
      var isOpen = btn.getAttribute('aria-expanded') === 'true';
      if (isOpen) btn.setAttribute('aria-expanded', 'false');
      else this.open(btn);
    }
  }

  /* ------------------------------------------------------------ reviews */

  class PchReviews extends Disposable(HTMLElement) {
    connectedCallback() {
      super.connectedCallback();
      var self = this;
      this.cards = $$('[data-pch-review]', this);
      this.empty = $('[data-pch-reviews-empty]', this);
      $$('[data-pch-filter]', this).forEach(function (btn) {
        self.on(btn, 'click', function () { self.apply(btn); });
      });
    }

    apply(btn) {
      var filter = btn.getAttribute('data-pch-filter');
      $$('[data-pch-filter]', this).forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      var shown = 0;
      this.cards.forEach(function (card) {
        var rating = card.getAttribute('data-rating');
        var hasMedia = card.getAttribute('data-has-media') === 'true';
        var match =
          filter === 'all' ? true :
          filter === 'media' ? hasMedia :
          rating === filter;
        card.hidden = !match;
        if (match) shown++;
      });
      if (this.empty) this.empty.hidden = shown > 0;
    }
  }

  /* ----------------------------------------------------------- register */

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  define('pch-announcement', PchAnnouncement);
  define('pch-header', PchHeader);
  define('pch-product', PchProduct);
  define('pch-faq', PchFaq);
  define('pch-reviews', PchReviews);

  // Sections without their own component still get scroll reveal.
  document.addEventListener('shopify:section:load', function (e) { revealIn(e.target); });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { revealIn(document); }, { once: true });
  } else {
    revealIn(document);
  }
})();
